/*
 * NavView — dumb view component for the unified nav.
 *
 * COPY CONTRACT: JS mirror of
 * `dex-monorepo/ui/apps/portal/src/app/components/NavView.tsx`. Keep in sync.
 *
 * Takes a pre-filtered `entries` tree + `activePathname` + optional `header` /
 * `footer` / `iconMap` / `onItemClick`. Renders clickable items as <a href>,
 * expandable groups as a clickable header + collapsible child list. Knows
 * nothing about claims, dex, features, profile, hooks.
 *
 * Item links are real <a href> (cross-app nav = full-page reload, and they stay
 * keyboard/middle-click friendly). The wrapper may pass `onItemClick(e, path)`
 * to intercept same-surface clicks for client-side routing (no reload); it
 * should leave cross-surface clicks alone so the href reload happens.
 *
 * Styling: reuses the legacy `.navigation-item*` SCSS classes (globally loaded
 * and themed per-dex) so the shell nav matches the legacy sidebar. The root
 * <nav> carries `cl-nav-view`, which gates the shell-only additions in
 * _navigation.scss (caret position/rotation + group-child indent). Only the
 * container layout stays inline; item/group visuals come from the classes.
 */

import { useState } from 'react';
import PropTypes from 'prop-types';

export const NAV_VIEW_WIDTH = 240;

function isPathActive(itemPath, pathname) {
	return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

function classes(...names) {
	return names.filter(Boolean).join(' ');
}

function renderIcon(iconKey, iconMap) {
	if (!iconKey || !iconMap) return null;
	const Icon = iconMap[iconKey];
	if (!Icon) return null;
	return <Icon />;
}

function NavItemRow({ item, activePathname, iconMap, depth, onItemClick }) {
	const active = isPathActive(item.path, activePathname);
	return (
		<>
			{item.uiDividerBefore && (
				// Divider above the item (e.g. Master Data Management vs the
				// dashboard links). Flag set by the portal's buildNav + carried in
				// navEntries; styled by `.cl-nav-view__divider` in _navigation.scss.
				<hr className="cl-nav-view__divider" />
			)}
			<a
				href={item.path}
				onClick={onItemClick ? (e) => onItemClick(e, item.path) : undefined}
				className={classes(
					'navigation-item',
					active && 'navigation-item--active',
					depth > 0 && 'navigation-item--child',
					depth > 0 && item.icon && 'navigation-item--child-icon',
				)}
			>
				<div className="navigation-item__side__rectangle" />
				<div className="navigation-item__icon">{renderIcon(item.icon, iconMap)}</div>
				<div className="navigation-item__label">{item.label}</div>
			</a>
		</>
	);
}

NavItemRow.propTypes = {
	item: PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		path: PropTypes.string.isRequired,
		icon: PropTypes.string,
	}).isRequired,
	activePathname: PropTypes.string.isRequired,
	iconMap: PropTypes.object,
	depth: PropTypes.number.isRequired,
	onItemClick: PropTypes.func,
};

function NavGroupRow({ group, activePathname, iconMap, onItemClick }) {
	// hasActiveChild only auto-expands the group; it must NOT highlight the group
	// header. Legacy nav (admin-ui/pitstop-ui Navigation) highlights only the
	// active leaf child — the expanded parent stays unhighlighted.
	const hasActiveChild = group.children.some((c) => isPathActive(c.path, activePathname));
	const [open, setOpen] = useState(hasActiveChild);

	const toggle = () => setOpen(!open);

	return (
		<div>
			<div
				className="navigation-item"
				role="button"
				tabIndex={0}
				onClick={toggle}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						toggle();
					}
				}}
			>
				<div className="navigation-item__side__rectangle" />
				<div className="navigation-item__icon">{renderIcon(group.icon, iconMap)}</div>
				<div className="navigation-item__label">{group.label}</div>
				<div
					className={classes(
						'navigation-item__toggle',
						open && 'navigation-item__chevron--rotate',
					)}
				>
					{renderIcon('caret-down', iconMap)}
				</div>
			</div>
			{open && (
				<div>
					{group.children.map((child) => (
						<NavItemRow
							key={child.key}
							item={child}
							activePathname={activePathname}
							iconMap={iconMap}
							depth={1}
							onItemClick={onItemClick}
						/>
					))}
				</div>
			)}
		</div>
	);
}

NavGroupRow.propTypes = {
	group: PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		icon: PropTypes.string,
		children: PropTypes.array.isRequired,
	}).isRequired,
	activePathname: PropTypes.string.isRequired,
	iconMap: PropTypes.object,
	onItemClick: PropTypes.func,
};

function NavView({ entries, activePathname, header, footer, iconMap, onItemClick }) {
	// Scroll fix: bound the nav to one viewport (height:100vh + overflow:hidden) and
	// let the item LIST scroll on its own (flex:1 + minHeight:0 + overflowY:auto), so
	// the pinned logo/footer stay put while overflowing items (e.g. Configuration +
	// Activity Log groups both expanded) remain reachable. Parent
	// `.DS__layout-left__side-nav` is position:fixed, so bounding height here keeps
	// the fixed sidebar exactly one viewport tall instead of growing off-screen.
	// (minHeight:0 is required — a flex child won't shrink below content otherwise,
	// which would defeat overflowY.)
	return (
		<nav
			className="cl-nav-view"
			style={{
				width: '100%',
				height: '100vh',
				display: 'flex',
				flexDirection: 'column',
				padding: '0.75rem 0',
				background: '#fff',
				overflow: 'hidden',
			}}
		>
			{header && (
				<div style={{ padding: '0.5rem 1rem 0.75rem', flexShrink: 0 }}>{header}</div>
			)}

			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
				}}
			>
				{entries.map((entry) =>
					entry.type === 'item' ? (
						<NavItemRow
							key={entry.key}
							item={entry}
							activePathname={activePathname}
							iconMap={iconMap}
							depth={0}
							onItemClick={onItemClick}
						/>
					) : (
						<NavGroupRow
							key={entry.key}
							group={entry}
							activePathname={activePathname}
							iconMap={iconMap}
							onItemClick={onItemClick}
						/>
					),
				)}
			</div>

			{footer && (
				<div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #ddd', flexShrink: 0 }}>
					{footer}
				</div>
			)}
		</nav>
	);
}

NavView.propTypes = {
	entries: PropTypes.array.isRequired,
	activePathname: PropTypes.string.isRequired,
	header: PropTypes.node,
	footer: PropTypes.node,
	iconMap: PropTypes.object,
	onItemClick: PropTypes.func,
};

export default NavView;
